'use client';

import React from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useTranslations } from 'next-intl';
import { ImageFragment } from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import OptimizedImage from '@/ui/components/shared/OptimizedImage';

export interface ProductImageLightboxProps {
  open: boolean;
  image?: ImageFragment;
  altText: string;
  onClose: () => void;
}

/**
 * Full-screen view of the hero image. Headless UI handles the Escape key, the
 * focus trap and the backdrop click, so the gallery only owns the open state.
 */
export function ProductImageLightbox({
  open,
  image,
  altText,
  onClose,
}: Readonly<ProductImageLightboxProps>) {
  const t = useTranslations('ClassicProductDetail');

  if (!image) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-neutral-100/80" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="relative h-full w-full max-w-screen-xl">
          <Dialog.Title className="sr-only">{altText}</Dialog.Title>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('closeFullscreenImage')}
            className="absolute right-0 top-0 z-10 rounded-xl bg-white p-2 duration-200 ease-in hover:bg-neutral-10"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
          <OptimizedImage
            image={image}
            alt={altText}
            fill
            sizes="100vw"
            className="object-contain"
          />
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
